// Legacy exception, not the preferred pattern: preserve this row's edit-button sizing
// until its layout is checked with the internal IconButton. Do not copy this import.
// nosemgrep: no-mui-icon-button
import { IconButton, Stack } from '@mui/material';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { InfoTooltip } from '@/components/poppers/Tooltip/InfoTooltip';
import { useFormContext } from '@/components/react-hook-form';
import {
  PrimaryCellTypography,
  SecondaryCellTypography,
} from '@/components/tables/DataTable/components/cells';
import { StyledTableCell } from '@/components/tables/DisplayTable/DisplayTable';
import { StyledTableRow } from '@/components/tables/DisplayTable/StyledTableRow';
import { CashflowFrequency } from '@/modules/cashflows/CashflowDrawer/CashflowDrawer.types';
import { formatCurrencyNoDecimalsAccounting } from '@/utils/formatting/currency';
import { formatPercentSmart } from '@/utils/formatting/percent';

import { CashflowsImportFormShape } from './CashflowsImport.types';
import { useCashflowsImportInferNameMatchLazyQuery } from './graphql/CashflowsImport.generated';

interface CashflowsImportRowProps {
  cashflowId: string;
  entityOptions: TypeaheadSelectInputOption<string>[];
  onEdit: (cashflowId: string) => void;
}

export function CashflowsImportRow({
  cashflowId,
  entityOptions,
  onEdit,
}: CashflowsImportRowProps) {
  const { control, getValues, setValue } =
    useFormContext<CashflowsImportFormShape>();
  const [isInferring, setIsInferring] = useState(false);
  const [inferNameMatch] = useCashflowsImportInferNameMatchLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const row = useWatch({
    control,
    name: `cashflows.${cashflowId}`,
  });
  const areDetailsDisabled = !row.shouldImport;
  const isMissingImportValue = row.amount == null && row.percentage == null;

  const description =
    row.frequency === CashflowFrequency.OneTime
      ? `One time in ${row.startYear}`
      : `Recurring from ${row.startYear} through ${row.endYear}`;
  const amount = row.amount
    ? formatCurrencyNoDecimalsAccounting(row.amount.abs())
    : '—';

  const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.checked) return;

    const currentRow = getValues(`cashflows.${cashflowId}`);
    if (currentRow.hasInferredTarget) return;

    setValue(`cashflows.${cashflowId}.hasInferredTarget`, true);
    if (!currentRow.ownerName.trim() || currentRow.targetId) return;

    const candidates = entityOptions.map(({ display, value }) => ({
      id: value,
      name: display,
    }));
    if (candidates.length === 0) return;

    setIsInferring(true);
    void inferNameMatch({
      variables: {
        input: { name: currentRow.ownerName, candidates },
      },
    })
      .then(({ data }) => {
        const targetId = data?.inferNameMatch;
        const latestRow = getValues(`cashflows.${cashflowId}`);
        if (targetId && latestRow.shouldImport && !latestRow.targetId) {
          setValue(`cashflows.${cashflowId}.targetId`, targetId, {
            shouldDirty: true,
          });
        }
      })
      .catch(() => undefined)
      .finally(() => setIsInferring(false));
  };

  return (
    <StyledTableRow>
      <StyledTableCell width={40} align="center">
        {isMissingImportValue ? (
          <InfoTooltip
            title="A non-zero amount or percentage is required to import and must be entered in eMoney"
            iconProps={{
              'aria-label': `Cannot import ${row.name}`,
              size: 24,
            }}
          />
        ) : (
          <FormAwareCheckbox<CashflowsImportFormShape>
            control={control}
            fieldName={`cashflows.${cashflowId}.shouldImport`}
            hideLabel
            ariaLabel={`Import ${row.name}`}
            onChange={handleSelection}
          />
        )}
      </StyledTableCell>
      <StyledTableCell>
        <Stack>
          <PrimaryCellTypography title={row.name}>
            {row.name}
          </PrimaryCellTypography>
          <SecondaryCellTypography>{description}</SecondaryCellTypography>
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <Stack>
          <PrimaryCellTypography>{amount}</PrimaryCellTypography>
          {row.growthRate && !row.growthRate.isZero() && (
            <SecondaryCellTypography>
              {formatPercentSmart(row.growthRate)}% growth
            </SecondaryCellTypography>
          )}
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <FormAwareTypeaheadSelectInput<CashflowsImportFormShape>
          control={control}
          fieldName={`cashflows.${cashflowId}.targetId`}
          label="Entity"
          hideLabel
          options={entityOptions}
          groupBy={(option) => option.groupName ?? ''}
          placeholder="Select entity"
          disabled={areDetailsDisabled || isInferring}
        />
      </StyledTableCell>
      <StyledTableCell width={48} align="center">
        <IconButton
          size="small"
          aria-label={`Edit ${row.name}`}
          onClick={() => onEdit(cashflowId)}
          disabled={areDetailsDisabled}
        >
          <Edit02Icon />
        </IconButton>
      </StyledTableCell>
    </StyledTableRow>
  );
}
